import { type Answer, type Finding, containsForbiddenXmlCharacter } from '@eformulieren/model'

/**
 * De controle op de tekenset: een antwoord mag geen teken bevatten dat XML 1.0 niet toelaat
 * (koppelvlak hoofdstuk 3, `xmlCharacters.ts` in het model).
 *
 * **Een harde controle die niet uit de definitie komt.** Elke andere controle is een regel die de
 * inrichter zet; deze geldt voor elk veld van elk formulier, want het bericht aan het processysteem
 * kan zo'n teken niet dragen, welk formulier het ook is. Ze heeft daarom een vaste regel-id die geen
 * gegenereerde id kan zijn (een gegenereerde id heeft tien tekens), en die id staat zo ook in de
 * voortgang die het processysteem opvraagt.
 *
 * **Waarom weigeren en niet stil weglaten.** De bouwers van het XML laten zo'n teken wel weg, voor wat
 * al is opgeslagen. Maar wat de invuller indient hoort te zijn wat hij ziet en wat het processysteem
 * krijgt; een teken dat onderweg verdwijnt is een verschil tussen die twee dat niemand heeft gezien.
 * De renderer laat zo'n teken bij plakken en typen al weg en zegt dat; deze controle is het slot
 * erachter, voor wat langs een andere weg in het document kwam, en draait in de browser en in de pod
 * met dezelfde functie (ontwerp 5.9).
 */

export const CHARACTER_CHECK_ID = 'rgl-tekens'

export const CHARACTER_CHECK: Finding = {
  regelId: CHARACTER_CHECK_ID,
  melding: {
    nl: 'Dit antwoord bevat een onzichtbaar teken dat niet kan worden verstuurd, meestal uit tekst die u hebt geplakt. Haal het weg, of typ dat stuk opnieuw.',
    en: 'This answer contains an invisible character that cannot be sent, usually from text you pasted. Remove it, or retype that part.',
  },
}

/**
 * Of dit antwoord zo'n teken bevat: in zijn waarde of waarden, in de structuur van rich text en in de
 * naam van een bijlage. De herkomst (`door`, `gewijzigdOp`) zet de pod en niet de invuller, en telt
 * niet mee; de rijen van een herhaalgroep toetst de evaluator per rijveld.
 */
export function hasForbiddenCharacter(answer: Answer | undefined): boolean {
  if (answer === undefined || 'rijen' in answer) return false
  return Object.entries(answer).some(([key, part]) => key !== 'door' && key !== 'gewijzigdOp' && containsForbiddenXmlCharacter(part))
}
